// ============================================
// Lab Analyst Stream — async generator wrapper
//
// Wraps runLabAnalyst() to yield incremental
// LabAnalystEvent objects for the Analyst Bar.
// ============================================

import type { BiomarkerResult, LabReport, LongitudinalBiomarkerData } from '../../types';
import type {
  LabAnalystEvent,
  BarMarker,
} from '../../types/analystBar';
import type { CapturedSignal } from '../../types/fashionPlate';
import type {
  AnalystPhase1Output,
  BiomarkerCard,
  SeverityLevel,
} from '../../types/patientJourney';
import { runLabAnalyst } from './labAnalyst';

// --- Helpers ---

/**
 * Map a BiomarkerResult to a display-ready BarMarker.
 *
 * hemologixStatus → flag mapping:
 *   optimal    → optimal
 *   suboptimal → borderline
 *   elevated   → borderline
 *   low        → attention
 *   critical   → attention
 */
export function mapBiomarkerToBarMarker(bio: BiomarkerResult): BarMarker {
  const flagMap: Record<BiomarkerResult['hemologixStatus'], BarMarker['flag']> = {
    optimal: 'optimal',
    suboptimal: 'borderline',
    elevated: 'borderline',
    low: 'attention',
    critical: 'attention',
  };

  return {
    id: bio.biomarker.toLowerCase().replace(/\s+/g, '-'),
    name: bio.biomarker,
    value: bio.value,
    flag: flagMap[bio.hemologixStatus],
  };
}

/**
 * Convert a full LabReport into the AnalystPhase1Output
 * consumed by the Analyst Bar and downstream protocol builder.
 */
export function mapReportToPhase1(report: LabReport): AnalystPhase1Output {
  const biomarkerCards: BiomarkerCard[] = report.biomarkers.map((bio) => ({
    biomarkerId: bio.biomarker.toLowerCase().replace(/\s+/g, '-'),
    name: bio.biomarker,
    value: bio.value,
    unit: bio.unit,
    standardRange: bio.standardRange,
    functionalRange: bio.functionalRange,
    optimalRange: bio.functionalRange, // MVP: same as functionalRange
    status: bio.hemologixStatus,
    trigger: bio.hemologixStatus !== 'optimal',
  }));

  const criticalMarkers = report.biomarkers
    .filter((b) => b.hemologixStatus === 'critical')
    .map((b) => b.biomarker);

  const triggeredCount = biomarkerCards.filter((c) => c.trigger).length;

  const severity: SeverityLevel =
    criticalMarkers.length >= 2
      ? 'critical'
      : criticalMarkers.length === 1
        ? 'urgent'
        : triggeredCount > 0
          ? 'attention'
          : 'routine';

  return {
    biomarkerCards,
    severity,
    triggeredCount,
    criticalMarkers,
  };
}

// --- Stream Generator ---

/**
 * Async generator that wraps runLabAnalyst() and yields
 * incremental LabAnalystEvent objects for the Analyst Bar
 * rendering surface.
 *
 * Event sequence:
 *   1. status  — "Analyzing biomarkers..."
 *   2. marker  — one per biomarker (progressive reveal)
 *   3. complete — full Phase 1 output
 */
export async function* streamLabAnalysis(
  file: File,
  mockMode: boolean,
  signals?: CapturedSignal[],
  priorData?: LongitudinalBiomarkerData | null,
  patientSex?: 'Male' | 'Female',
): AsyncGenerator<LabAnalystEvent> {
  // 1. Status: analysis starting
  yield { type: 'status', message: 'Analyzing biomarkers against functional optimal ranges' };

  // 2. Run the lab analyst (vision API or mock), passing captured signals + prior data + sex
  const report = await runLabAnalyst(file, mockMode, signals, priorData, patientSex);

  // 3. Yield each biomarker as a BarMarker
  for (const bio of report.biomarkers) {
    yield { type: 'marker', marker: mapBiomarkerToBarMarker(bio) };
  }

  // 4. Yield the complete Phase 1 output
  yield { type: 'complete', report: mapReportToPhase1(report) };
}
